import MagentoOrderRequestBuilder from './request-builder/magento-order';
import PaymentDetailsRequestBuilder from './request-builder/payment-details';
import emit from '../../common/events';
import store from '../../store/store';
import { BaseComponent } from '../base-component/base-component';
import { CountryCode } from '../country-selector/country-state.interface';
import { OrderCreator } from './utils/order-creator.util';
import { PaymentMethodType } from '../payment-methods/payment-method';
import { StoreState } from '../../store/store.interface';
import { TaxInfo } from '../tax-info/tax-info';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getCart, getProductCountryCode, getProductsFromCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { getFreeTrialProduct } from '../free-trial/utils/free-trial.util';
import { getIsOrderPlacementDisabled, getIsPlacingOrder } from './utils/order-placement.util';
import { getActiveStepId, getPaymentDetails, getPoOrderNumber, isCheckoutComplete, isPurchaseOrderApplied, isShippingAddressValid } from '../checkout/shop-checkout';
import { getUserDataForUserAnalytics } from '../user-profile/user-profile.selectors';
import { css, html } from 'lit';
import { isPaypalPaymentMethod } from '../../common/utils/payment-util';
import { isTunnelPurchaseFlow } from '../../common/utils/util';
import { isTunnelTaxSupportedCountry } from '../tax-info/tax-info-options';
import { nothing } from 'lit-html';
import { smallSpinner, loading } from '../../common/constants/api-constants';
import {
  addAnalyticsCheckoutFlowMetadata,
  getSuccessUrl,
  hasAtLeastOneFreeTrialProduct
} from '../../common/utils/cart-util';

/**
 * A container for checkout agreements, tax information and the place order button.
 */
@customElement('mhecomm-order-placement')
export class OrderPlacement extends connect(store)(BaseComponent) {
  static styles = [css`
    .place-order-agreements {
      font-size: .75rem;
      line-height: 1.25rem;
    }
  `];

  /**
   * Place Order Button
   */
  @property()
  placeOrderButtonName = 'Place Order';

  /**
   * Store code related to the checkout process. If not set it will be the national process.
   */
  @property()
  store?: string;

  /**
   * Country code of the order. Default US.
   */
  @state()
  countryCode = CountryCode.US as string;

  /**
   * Flag to disable the place order button.
   */
  @state()
  private orderPlacementDisabled = false;

  /**
   * Flag to indicate if the order is currently being placed.
   */
  @state()
  private isPlacingOrder = false;

  /**
   * Flag to indicate if the checkout is complete (meaning the order review step is shown).
   */
  @state()
  private isCheckoutComplete = false;

  /**
   * Flag to indicate if activeStepId is set
   */
  @state()
  private isActiveStateId = false;

  /**
   * Setups order placement component.
   *
   * @param _changedProperties
   */
  protected async firstUpdated(_changedProperties: Map<string | number | symbol, unknown>) {
    super.firstUpdated(_changedProperties);
    this.stateInit();
  }

  /**
   * Initializes the component state.
   */
  stateInit() {
    const state = store.getState() as StoreState;
    const cart = getCart(store.getState() as StoreState);
    if (!cart) {
      return
    }

    const isShippable = cart.shippable || false;
    const paymentDetails = getPaymentDetails(state);

    if (
      isPaypalPaymentMethod(paymentDetails)
      && state.shopCheckoutState.shippingAddress
      && state.shopCheckoutState.billingAddress
    ) {
      this.orderPlacementDisabled = isShippable ? !isShippingAddressValid(store.getState() as StoreState) : false
    }
  }

  /**
   * Updates internal variables every time the state changes.
   *
   * @param state
   */
  stateChanged(state: StoreState) {
    this.countryCode = getProductCountryCode(state);
    this.isPlacingOrder = getIsPlacingOrder();
    this.orderPlacementDisabled = getIsOrderPlacementDisabled();
    this.isCheckoutComplete = isCheckoutComplete(state);
    this.isActiveStateId = !!getActiveStepId(state);
  }

  renderComp() {
    return html`
      ${this.isCheckoutComplete
        ? html`
          ${this.getTunnelTaxInfo()}
          ${this.renderTerms()}
          <div class="place-order-agreements my-1">
            ${this.renderAgreements()}
          </div>
          <div class="mt-3">
            <button
              id="btn-place-order"
              type="button"
              class="btn btn-lg btn-primary w-100"
              ?disabled=${this.orderPlacementDisabled || this.isPlacingOrder}
              @click=${this.onClickCompleteCheckoutButton}>
              ${this.placeOrderButtonName}
              ${this.isPlacingOrder ? smallSpinner() : nothing}
            </button>
          </div>
        ` :
        (!this.isActiveStateId) ?
          html`
            <span class="visually-hidden">Loading checkout step</span>
            ${loading()}
            ${loading({m: 'my-3'})}` :
        nothing}
    `;
  }

  /**
   * Renders the tax info component if the country is supported.
   *
   * @private
   */
  private getTunnelTaxInfo() {
    if (!isTunnelTaxSupportedCountry(this.countryCode)) {
      return nothing;
    }

    return html`
      <mhecomm-tax-info
        userInternalErrorMessageModel="true"
        country="${this.countryCode}"
      ></mhecomm-tax-info>`
  }

  /**
   * Renders the terms and conditions for free trial products.
   *
   * @private
   */
  private renderTerms() {
    if (!hasAtLeastOneFreeTrialProduct(getProductsFromCart(store.getState() as StoreState))) {
      return html``;
    }

    return html`
      <div class="my-2">
        <mhecomm-free-trial-terms/>
      </div>
    `;
  }

  /**
   * Renders base agreements.
   *
   * If there is a free trial product in the cart, it will also render the free trial agreements.
   *
   * @private
   */
  private renderAgreements() {
    const freeTrialProduct = getFreeTrialProduct();

    if (freeTrialProduct) {
      return html`
        <div class="ft-agreements">
          <mhecomm-free-trial-agreements
            .freeTrial=${freeTrialProduct.freeTrialInfo}
            .showCancellationInfo="${false}">
          </mhecomm-free-trial-agreements>
        </div>
      `;
    }

    return html`
      <mhecomm-checkout-agreements/>`
  }

  /**
   * Processes the payment and creates the order for either Magento or Shop.
   *
   * If the payment details are missing, we will not proceed with the order creation and a message will be displayed.
   *
   * @param _e
   */
  async onClickCompleteCheckoutButton(_e: Event) {
    const state = store.getState() as StoreState;
    const orderCreator = new OrderCreator(this.store);

    if (isTunnelTaxSupportedCountry(this.countryCode)) {
      const taxInfoForm = this.shadowRoot!.querySelector("mhecomm-tax-info") as TaxInfo;
      await taxInfoForm.validate();
    }

    // [SHOP-API-REMOVAL][WECOMM-20796] Shop order-request builder removed; always use Magento.
    const orderRequest = new MagentoOrderRequestBuilder(new PaymentDetailsRequestBuilder()).build();

    if (orderRequest) {
      this.placeOrderAnalytics(state);
      orderCreator.createOrder(orderRequest);
    }
  }

  /**
   * Sends analytics event for place order.
   *
   * @param state
   * @private
   */
  private placeOrderAnalytics(state: StoreState) {
    const details: any = addAnalyticsCheckoutFlowMetadata({
      user: getUserDataForUserAnalytics(state),
      url: getSuccessUrl(this.store),
      subarea: "checkout-steps",
      paymentType: PaymentMethodType.getPaymentMethodType(getPaymentDetails(state)?.paymentType!)?.displayName,
      ...(!!getPoOrderNumber(state) && isPurchaseOrderApplied(state) && 
      { 
        paymentType: "Purchase Order", 
        purchaseOrderNumber: getPoOrderNumber(state) 
      })
    }, true);

    if (isTunnelPurchaseFlow()) {
      const product = getCart(state)?.products[0]!;
      details.countryCode = product.priceInfo.countryCode
    }

    emit({
      element: this,
      name: 'place-order',
      detail: details
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-order-placement': OrderPlacement;
  }
}
